(function () {
  'use strict';

  var existing = window.AnconaObsidianEdit;
  if (existing && typeof existing.init === 'function') {
    existing.init(document);
    return;
  }

  var states = new WeakMap();
  var ROOT_SELECTOR = '[data-ancona-edit]';
  var RESULT_LIMIT = 4;
  var PROMPT_LIMIT = 240;

  function all(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function rootsIn(scope) {
    var roots = [];
    if (scope && scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope && scope.querySelectorAll) {
      all(scope, ROOT_SELECTOR).forEach(function (root) {
        if (roots.indexOf(root) === -1) roots.push(root);
      });
    }
    return roots;
  }

  function parseCandidates(node) {
    var parsed;
    try {
      parsed = JSON.parse(node.textContent || '[]');
    } catch (error) {
      return [];
    }

    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter(function (candidate) {
        return candidate &&
          String(candidate.productId || '') !== '' &&
          String(candidate.variantId || '') !== '' &&
          typeof candidate.handle === 'string' &&
          candidate.handle !== '' &&
          typeof candidate.title === 'string' &&
          candidate.title !== '' &&
          typeof candidate.url === 'string' &&
          candidate.url !== '' &&
          typeof candidate.price === 'string' &&
          Number.isFinite(Number(candidate.priceMax)) &&
          Number(candidate.priceMax) >= 0 &&
          typeof candidate.category === 'string' &&
          candidate.category !== '' &&
          typeof candidate.mood === 'string' &&
          candidate.mood !== '' &&
          typeof candidate.image540 === 'string' &&
          candidate.image540 !== '';
      })
      .map(function (candidate, index) {
        candidate.order = Number.isFinite(Number(candidate.order)) ? Number(candidate.order) : index;
        candidate.priceMax = Number(candidate.priceMax);
        return candidate;
      });
  }

  function templateIsComplete(template) {
    var required = [
      '[data-edit-card]',
      '[data-edit-product-link]',
      '[data-edit-image]',
      '[data-edit-match-note]',
      '[data-edit-index]',
      '[data-edit-category-label]',
      '[data-edit-mood-label]',
      '[data-edit-title-link]',
      '[data-edit-price]',
      '[data-edit-view-link]',
      '[data-av5-book]',
      '[data-av5-book-label]',
      '[data-av8-compare-toggle]',
      '[data-av8-compare-label]'
    ];
    return required.every(function (selector) {
      return template.content.querySelector(selector);
    });
  }

  function createState(root) {
    var state = {
      root: root,
      fallback: root.querySelector('[data-edit-fallback]'),
      wizard: root.querySelector('[data-edit-wizard]'),
      form: root.querySelector('[data-edit-form]'),
      panels: all(root, '[data-edit-panel]'),
      progress: all(root, '[data-edit-progress]'),
      progressList: root.querySelector('.aoe-progress'),
      candidateNode: root.querySelector('[data-edit-candidates]'),
      template: root.querySelector('[data-edit-card-template]'),
      results: root.querySelector('[data-edit-results]'),
      resultsTitle: root.querySelector('[data-edit-results-title]'),
      summary: root.querySelector('[data-edit-summary]'),
      empty: root.querySelector('[data-edit-empty]'),
      grid: root.querySelector('[data-edit-grid]'),
      browse: root.querySelector('[data-edit-browse]'),
      ai: root.querySelector('[data-edit-ai]'),
      refine: root.querySelector('[data-edit-refine]'),
      status: root.querySelector('[data-edit-status]'),
      selection: null,
      step: 0,
      controller: null
    };

    var required = [
      state.fallback,
      state.wizard,
      state.form,
      state.candidateNode,
      state.template,
      state.results,
      state.resultsTitle,
      state.summary,
      state.empty,
      state.grid,
      state.browse,
      state.ai,
      state.refine,
      state.status
    ];

    if (
      required.some(function (node) { return !node; }) ||
      state.panels.length !== 3 ||
      state.progress.length !== 3 ||
      !state.panels.every(function (panel) {
        return panel.querySelector('[data-edit-step-focus]') &&
          panel.querySelector('[data-edit-choice]') &&
          panel.querySelector('[data-edit-error]');
      }) ||
      !templateIsComplete(state.template)
    ) {
      return null;
    }

    state.candidates = parseCandidates(state.candidateNode);
    if (!state.candidates.length) return null;

    try {
      state.controller = new AbortController();
    } catch (error) {
      return null;
    }

    return state;
  }

  function announce(state, message) {
    state.status.textContent = '';
    window.requestAnimationFrame(function () {
      if (state.status.isConnected) state.status.textContent = message || '';
    });
  }

  function focusNode(node, reveal) {
    window.requestAnimationFrame(function () {
      if (!node || !node.isConnected) return;
      try {
        node.focus({ preventScroll: true });
      } catch (error) {
        node.focus();
      }
      if (reveal) node.scrollIntoView({ block: 'start', behavior: 'auto' });
    });
  }

  function showStep(state, nextStep, shouldFocus) {
    state.step = Math.max(0, Math.min(2, nextStep));
    state.form.hidden = false;
    state.results.hidden = true;
    if (state.progressList) state.progressList.hidden = false;
    state.wizard.classList.remove('aoe-wizard--results');

    state.panels.forEach(function (panel, index) {
      panel.hidden = index !== state.step;
    });
    state.progress.forEach(function (item, index) {
      if (index === state.step) item.setAttribute('aria-current', 'step');
      else item.removeAttribute('aria-current');
    });

    if (shouldFocus) {
      focusNode(state.panels[state.step].querySelector('[data-edit-step-focus]'), true);
    }
  }

  function selectedChoice(state, step) {
    return state.panels[step].querySelector('[data-edit-choice]:checked');
  }

  function setPanelValidity(state, step, valid) {
    var panel = state.panels[step];
    var error = panel.querySelector('[data-edit-error]');

    panel.setAttribute('aria-invalid', valid ? 'false' : 'true');
    all(panel, '[data-edit-choice]').forEach(function (choice) {
      choice.setAttribute('aria-invalid', valid ? 'false' : 'true');
    });
    error.hidden = valid;
  }

  function validateStep(state, step) {
    var valid = !!selectedChoice(state, step);
    setPanelValidity(state, step, valid);
    return valid;
  }

  function choiceValue(state, step) {
    var choice = selectedChoice(state, step);
    if (!choice) return null;
    return {
      value: choice.value,
      label: choice.getAttribute('data-choice-label') || choice.value
    };
  }

  function readSelection(state) {
    var category = choiceValue(state, 0);
    var budget = choiceValue(state, 1);
    var mood = choiceValue(state, 2);
    if (!category || !budget || !mood) return null;
    return {
      category: category,
      budget: budget,
      mood: mood,
      ceiling: Number(budget.value) || 0
    };
  }

  function byMoodThenOrder(selection) {
    return function (first, second) {
      var firstMood = first.mood === selection.mood.value ? 0 : 1;
      var secondMood = second.mood === selection.mood.value ? 0 : 1;
      if (firstMood !== secondMood) return firstMood - secondMood;
      return first.order - second.order;
    };
  }

  function diversifyCategories(candidates, limit) {
    var categoryOrder = [];
    var queues = new Map();
    var output = [];
    var advanced = true;

    candidates.forEach(function (candidate) {
      if (!queues.has(candidate.category)) {
        queues.set(candidate.category, []);
        categoryOrder.push(candidate.category);
      }
      queues.get(candidate.category).push(candidate);
    });

    while (output.length < limit && advanced) {
      advanced = false;
      categoryOrder.forEach(function (category) {
        var queue = queues.get(category);
        if (output.length >= limit || !queue || !queue.length) return;
        output.push(queue.shift());
        advanced = true;
      });
    }

    return output;
  }

  function rankCandidates(candidates, selection) {
    var eligible = candidates
      .filter(function (candidate) {
        return selection.ceiling === 0 || candidate.priceMax <= selection.ceiling;
      })
      .sort(byMoodThenOrder(selection));

    if (selection.category.value === 'any') {
      var diversified = diversifyCategories(eligible, RESULT_LIMIT);
      return {
        exact: diversified,
        alternatives: [],
        items: diversified,
        isAny: true
      };
    }

    var exact = eligible
      .filter(function (candidate) {
        return candidate.category === selection.category.value;
      })
      .slice(0, RESULT_LIMIT);
    var slots = Math.max(0, RESULT_LIMIT - exact.length);
    var alternatives = diversifyCategories(
      eligible.filter(function (candidate) {
        return candidate.category !== selection.category.value;
      }),
      slots
    );

    return {
      exact: exact,
      alternatives: alternatives,
      items: exact.concat(alternatives),
      isAny: false
    };
  }

  function labelFor(state, type, value) {
    var choices = all(state.root, '[data-edit-choice="' + type + '"]');
    for (var index = 0; index < choices.length; index += 1) {
      if (choices[index].value === value) {
        return choices[index].getAttribute('data-choice-label') || value;
      }
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  function setHref(node, href) {
    node.setAttribute('href', href);
  }

  function buildCard(state, candidate, index, kind) {
    var fragment = state.template.content.cloneNode(true);
    var card = fragment.querySelector('[data-edit-card]');
    var mediaLink = fragment.querySelector('[data-edit-product-link]');
    var image = fragment.querySelector('[data-edit-image]');
    var matchNote = fragment.querySelector('[data-edit-match-note]');
    var titleLink = fragment.querySelector('[data-edit-title-link]');
    var viewLink = fragment.querySelector('[data-edit-view-link]');
    var save = fragment.querySelector('[data-av5-book]');
    var tray = fragment.querySelector('[data-av8-compare-toggle]');
    var saveLabel = state.root.dataset.saveLabel || 'Save';
    var savedLabel = state.root.dataset.savedLabel || 'Saved';
    var trayAddLabel = tray.dataset.av8LabelAdd || 'Place on Private Tray';

    card.dataset.productId = String(candidate.productId);
    card.dataset.productHandle = candidate.handle;
    card.dataset.productTitle = candidate.title;
    card.dataset.editResultKind = kind;

    matchNote.textContent = kind === 'alternative'
      ? (state.root.dataset.alternativesLabel || 'Product suggestion')
      : labelFor(state, 'category', candidate.category);

    setHref(mediaLink, candidate.url);
    setHref(titleLink, candidate.url);
    setHref(viewLink, candidate.url);
    setHref(save, candidate.url);
    setHref(tray, candidate.url);

    image.src = candidate.image540;
    image.alt = candidate.imageAlt || candidate.title;
    image.width = Math.max(1, Number(candidate.imageWidth) || 540);
    image.height = Math.max(1, Number(candidate.imageHeight) || 540);
    if (candidate.image360 && candidate.image720) {
      image.setAttribute(
        'srcset',
        candidate.image360 + ' 360w, ' + candidate.image540 + ' 540w, ' + candidate.image720 + ' 720w'
      );
      image.setAttribute(
        'sizes',
        '(min-width: 1100px) 25vw, (min-width: 600px) 50vw, 50vw'
      );
    }

    fragment.querySelector('[data-edit-index]').textContent = String(index + 1).padStart(2, '0');
    fragment.querySelector('[data-edit-category-label]').textContent =
      labelFor(state, 'category', candidate.category);
    fragment.querySelector('[data-edit-mood-label]').textContent =
      labelFor(state, 'mood', candidate.mood);
    titleLink.textContent = candidate.title;
    fragment.querySelector('[data-edit-price]').textContent = candidate.price;

    save.dataset.av5LabelSave = saveLabel;
    save.dataset.av5LabelSaved = savedLabel;
    save.dataset.av5ProductId = String(candidate.productId);
    save.dataset.av5Handle = candidate.handle;
    save.dataset.av5VariantId = String(candidate.variantId);
    save.dataset.av5Title = candidate.title;
    save.dataset.av5Url = candidate.url;
    save.dataset.av5Price = candidate.price;
    save.dataset.av5Image = candidate.bookImage || candidate.image360 || candidate.image540;
    save.setAttribute('aria-label', saveLabel + ' — ' + candidate.title);
    fragment.querySelector('[data-av5-book-label]').textContent = saveLabel;

    tray.dataset.handle = candidate.handle;
    tray.dataset.productId = String(candidate.productId);
    tray.dataset.productTitle = candidate.title;
    tray.setAttribute('aria-label', trayAddLabel + ' — ' + candidate.title);

    return fragment;
  }

  function conciergePrompt(selection) {
    var prompt =
      'Help me refine my Ancona Edit. Category: ' + selection.category.label +
      '. Maximum spend: ' + selection.budget.label +
      '. Mood: ' + selection.mood.label +
      '. Please ask what I would like to adjust.';
    return prompt.slice(0, PROMPT_LIMIT);
  }

  function updateSummary(state, ranked, selection) {
    if (ranked.isAny) {
      state.summary.textContent = ranked.items.length + ' · ' + selection.mood.label;
      return;
    }

    var alternativesLabel = state.root.dataset.alternativesLabel || 'Product suggestions';
    var summary = ranked.exact.length + ' · ' + selection.category.label;
    if (ranked.alternatives.length) {
      summary += ' / ' + ranked.alternatives.length + ' · ' + alternativesLabel;
    }
    state.summary.textContent = summary;
  }

  function renderResults(state, selection) {
    announce(state, state.root.dataset.statusFinding || 'Finding pieces based on your choices.');
    state.grid.setAttribute('aria-busy', 'true');
    state.grid.replaceChildren();

    var ranked = rankCandidates(state.candidates, selection);
    var fragment = document.createDocumentFragment();
    var cardIndex = 0;

    ranked.exact.forEach(function (candidate) {
      fragment.appendChild(buildCard(state, candidate, cardIndex, 'exact'));
      cardIndex += 1;
    });

    if (ranked.alternatives.length) {
      ranked.alternatives.forEach(function (candidate) {
        fragment.appendChild(buildCard(state, candidate, cardIndex, 'alternative'));
        cardIndex += 1;
      });
    }

    state.grid.appendChild(fragment);
    state.grid.setAttribute('aria-busy', 'false');
    state.empty.hidden = ranked.items.length !== 0;
    updateSummary(state, ranked, selection);

    var route =
      state.root.getAttribute('data-route-' + selection.category.value) ||
      state.root.dataset.routeAny ||
      '/collections/all';
    state.browse.setAttribute('href', route);
    state.ai.setAttribute('data-av5-concierge-query', conciergePrompt(selection));
    state.selection = selection;
    state.form.hidden = true;
    state.results.hidden = false;
    if (state.progressList) state.progressList.hidden = true;
    state.wizard.classList.add('aoe-wizard--results');

    if (ranked.items.length) {
      var readyTemplate = state.root.dataset.statusReadyTemplate || '__COUNT__ product suggestions shown.';
      announce(
        state,
        readyTemplate.replace(/__COUNT__|\{count\}/g, String(ranked.items.length))
      );
    } else {
      announce(
        state,
        state.root.dataset.statusEmpty ||
          'No current pieces fit this ceiling. Alternative shopping paths follow.'
      );
    }

    focusNode(state.resultsTitle, true);
    document.dispatchEvent(new CustomEvent('ancona:v5:grid-rendered'));
  }

  function disableEnhancement(state) {
    if (state.controller) state.controller.abort();
    state.root.removeAttribute('data-edit-ready');
    state.wizard.hidden = true;
    state.fallback.hidden = false;
    states.delete(state.root);
  }

  function handleClick(state, event) {
    var target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    var next = target.closest('[data-edit-next]');
    if (next && state.root.contains(next)) {
      if (!validateStep(state, state.step)) {
        focusNode(state.panels[state.step].querySelector('[data-edit-step-focus]'));
        return;
      }
      showStep(state, state.step + 1, true);
      return;
    }

    var back = target.closest('[data-edit-back]');
    if (back && state.root.contains(back)) {
      showStep(state, state.step - 1, true);
      return;
    }

    var refine = target.closest('[data-edit-refine]');
    if (refine && state.root.contains(refine)) {
      showStep(state, 0, true);
      return;
    }

    if (target.closest('[data-edit-ai]')) {
      var selection = state.selection || readSelection(state);
      if (selection) state.ai.setAttribute('data-av5-concierge-query', conciergePrompt(selection));
    }
  }

  function handleSubmit(state, event) {
    event.preventDefault();
    for (var step = 0; step < 3; step += 1) {
      if (!validateStep(state, step)) {
        showStep(state, step, true);
        return;
      }
    }

    var selection = readSelection(state);
    if (!selection) return;
    try {
      renderResults(state, selection);
    } catch (error) {
      disableEnhancement(state);
    }
  }

  function initRoot(root) {
    if (states.has(root)) return;

    var state = createState(root);
    if (!state) return;
    var signal = state.controller.signal;

    root.addEventListener('click', function (event) {
      try {
        handleClick(state, event);
      } catch (error) {
        disableEnhancement(state);
      }
    }, { signal: signal });

    state.form.addEventListener('submit', function (event) {
      handleSubmit(state, event);
    }, { signal: signal });

    state.form.addEventListener('change', function (event) {
      var choice = event.target instanceof Element
        ? event.target.closest('[data-edit-choice]')
        : null;
      if (!choice) return;

      var panel = choice.closest('[data-edit-panel]');
      var panelIndex = state.panels.indexOf(panel);
      if (panelIndex !== -1) setPanelValidity(state, panelIndex, true);

      var selection = readSelection(state);
      if (selection) state.ai.setAttribute('data-av5-concierge-query', conciergePrompt(selection));
    }, { signal: signal });

    states.set(root, state);
    showStep(state, 0, false);
    state.wizard.hidden = false;
    state.fallback.hidden = true;
    root.dataset.editReady = 'true';
  }

  function init(scope) {
    rootsIn(scope || document).forEach(initRoot);
  }

  function unload(scope) {
    rootsIn(scope || document).forEach(function (root) {
      var state = states.get(root);
      if (!state) return;
      state.controller.abort();
      root.removeAttribute('data-edit-ready');
      states.delete(root);
    });
  }

  window.AnconaObsidianEdit = {
    version: '1.1.0',
    init: init,
    unload: unload
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    }, { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    unload(event.target);
  });
}());
